/**
 * The page-level state behind a creation route that fills a fresh kit: the skeleton it shows while
 * the assets land, and the "Do they look right?" toast it raises once they have.
 */

import {setBrandKitExtractionFeedbackSource, setBrandKitExtractionInProgress} from '@Components/mystuff-brands/brands-reducer';
import type {BrandKitExtractionSource} from '@Components/mystuff-brands/brand-kits.types';
import type {BrandKitExtractionNegativeFeedback} from '@Components/mystuff-brands/components/brand-kit-extraction-feedback-toast/brand-kit-extraction-feedback-toast.types';
import {BrandKitExtractionFeedbackVerdict} from '@Components/mystuff-brands/components/brand-kit-extraction-feedback-toast/brand-kit-extraction-feedback-toast.types';
import {BrandKitExtractionFeedbackGA4Type, GA4EventName, GA4EventParamName, trackGA4CustomEvent} from '@Libraries/ga-events';

const EXTRACTION_FEEDBACK_GA4_TYPES: Record<BrandKitExtractionFeedbackVerdict, BrandKitExtractionFeedbackGA4Type> = {
  [BrandKitExtractionFeedbackVerdict.POSITIVE]: BrandKitExtractionFeedbackGA4Type.THUMBS_UP,
  [BrandKitExtractionFeedbackVerdict.NEGATIVE]: BrandKitExtractionFeedbackGA4Type.THUMBS_DOWN,
};

/** Raises the toast for the route that just filled the kit; a later call for another route replaces it. */
export const showBrandKitExtractionFeedbackToast = (source: BrandKitExtractionSource): void => {
  window.PMW.redux.store.dispatch(setBrandKitExtractionFeedbackSource(source));
};

export const dismissBrandKitExtractionFeedbackToast = (): void => {
  window.PMW.redux.store.dispatch(setBrandKitExtractionFeedbackSource(null));
};

/** Records the thumb for GA4; the toast stays up for whatever follows the thumb. */
export const trackBrandKitExtractionFeedback = (source: BrandKitExtractionSource, verdict: BrandKitExtractionFeedbackVerdict): void => {
  trackGA4CustomEvent(GA4EventName.BRAND_KIT_EXTRACTION_FEEDBACK, {[GA4EventParamName.TYPE]: EXTRACTION_FEEDBACK_GA4_TYPES[verdict], [GA4EventParamName.SOURCE]: source});
};

/** Records the thumb for GA4 and takes the toast down: the question is asked once per extraction. */
export const recordBrandKitExtractionFeedback = (source: BrandKitExtractionSource, verdict: BrandKitExtractionFeedbackVerdict): void => {
  trackBrandKitExtractionFeedback(source, verdict);
  dismissBrandKitExtractionFeedbackToast();
};

/**
 * A thumbs-down sent once its reason is picked: the plain thumb's GA4 event plus the reason, with reason and note stored server-side.
 * The toast stays for the following steps; a failed store is not worth interrupting them over.
 */
export const recordNegativeBrandKitExtractionFeedbackWithReason = ({source, reason, note}: BrandKitExtractionNegativeFeedback): void => {
  trackGA4CustomEvent(GA4EventName.BRAND_KIT_EXTRACTION_FEEDBACK, {
    [GA4EventParamName.TYPE]: BrandKitExtractionFeedbackGA4Type.THUMBS_DOWN,
    [GA4EventParamName.SOURCE]: source,
    [GA4EventParamName.REASON]: reason,
  });
  void window.PMW.writeLocal('brand/recordExtractionFeedback', {source, reason, note});
};

/** The page shows its skeleton from here until finishBrandKitExtraction(), instead of a half-filled bento. */
export const startBrandKitExtraction = (): void => {
  window.PMW.redux.store.dispatch(setBrandKitExtractionInProgress(true));
};

export const finishBrandKitExtraction = (): void => {
  window.PMW.redux.store.dispatch(setBrandKitExtractionInProgress(false));
};
